﻿import { EventTypeTag } from "@/shared/components/EventTypeTag";
import { billetStatus } from "@/shared/selectors/BilletStatusSelector";
import { eventTypes } from "@/shared/selectors/EventTypeSelector";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { DragIndicator, Edit as EditIcon } from "@mui/icons-material";
import {
	Box,
	IconButton,
	lighten,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { ColumnDef, ColumnGroup, ProcessedRow } from "../models/utils";
import {
	getEventMonthScheduledRatio,
	isMultiMonthEvent as _isMultiMonth,
} from "../utils/calculateFields";
import { resolveTargetTPH } from "../utils/targetTPH";
import React from "react";

const formatTons = (value: number | null | undefined): string => {
	if (!value) return "";
	if (typeof value !== "number" || !isFinite(value)) return "";
	return value.toLocaleString("en-US", {
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	});
};

const formatPercent = (value: number | null | undefined): string => {
	if (!value) return "";
	if (typeof value !== "number" || !isFinite(value)) return "";
	return `${value.toFixed(2)}%`;
};

const getRowBackgroundColor = ({
	row,
	hoveredBilletStatusCode,
	hoveredEventTypeCode,
	useColors,
}: {
	row: ProcessedRow;
	hoveredBilletStatusCode?: string | null;
	hoveredEventTypeCode?: string | null;
	useColors?: boolean;
}): string => {
	const isPlaceholder = row.EventId < 0;
	const isHoveredBillet =
		hoveredBilletStatusCode &&
		row.BilletsStatusCode === hoveredBilletStatusCode;
	const isHoveredEventType =
		hoveredEventTypeCode && row.EventTypeCode === hoveredEventTypeCode;

	if (row.IsMTDSummary) return "#FFEB3B";
	if (row.IsMonthSummary) return "#E3F2FD";
	if (isPlaceholder) return "#f5f5f5";

	if (useColors && row.SectionId) {
		const goldenRatio = 0.618033988749;
		const hue = (row.SectionId * goldenRatio * 360) % 360;
		const saturation = 40 + ((row.SectionId * 17) % 20);
		const lightness = 70 + ((row.SectionId * 13) % 15);
		return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
	}

	const eventType = eventTypes.find((e) => e.code === row.EventTypeCode);
	const billetstatus = billetStatus.find(
		(s) => s.code === row.BilletsStatusCode,
	);

	if (isHoveredEventType) return eventType?.backgroundColor ?? "#fcfcfcff";
	if (isHoveredBillet) return billetstatus?.hoverColor ?? "#fcfcfcff";

	return "#fcfcfcff";
};

// Use OriginalStart/OriginalEnd (actual event dates) — row.Start/End are day-sliced calendar boundaries.
const isMultiMonthEvent = (row: ProcessedRow): boolean =>
	_isMultiMonth(row.OriginalStart ?? row.Start, row.OriginalEnd ?? row.End);

const getMonthRatio = (row: ProcessedRow, calendarMonth: number): number => {
	const start = row.OriginalStart ?? row.Start;
	const end = row.OriginalEnd ?? row.End;
	if (!start || !end) return 1;
	return getEventMonthScheduledRatio(
		start,
		end,
		calendarMonth,
		row.ScheduledRollingHours,
	);
};

const renderMonthAndTotal = (
	monthValue: string,
	totalValue: string | null,
	isMultiMonth: boolean,
): React.ReactNode => {
	if (!isMultiMonth || !totalValue) return monthValue || null;
	return (
		<Box sx={{ lineHeight: 1.3 }}>
			<Box>{monthValue}</Box>
			<Typography
				variant="caption"
				color="text.secondary"
				sx={{ fontSize: "0.72rem" }}
			>
				({totalValue})
			</Typography>
		</Box>
	);
};

interface useCustomTableColumnsProps {
	handleEditClick?: (eventId: number) => void;
	onSectionClick?: (
		event: ProcessedRow,
		position: { x: number; y: number },
	) => void;
	isDraftMode?: boolean;
	calendarMonth?: number;
	canEditSchedule?: boolean;
}

const getEffectiveTargetTPH = (row: ProcessedRow): number | null | undefined =>
	row.EffectiveTargetTPH ??
	resolveTargetTPH({
		TargetTPH: row.TargetTPH,
		SectionTargetTPH: row.SectionTargetTPH,
		TargetTPHPercentage: row.TargetTPHPercentage,
		SectionTargetTPHPercentage: row.SectionTargetTPHPercentage,
	});

export const useCustomTableColumns = ({
	handleEditClick,
	onSectionClick,
	isDraftMode = false,
	calendarMonth = new Date().getMonth(),
	canEditSchedule = false,
}: useCustomTableColumnsProps) => {
	const theme = useTheme();

	const columnGroups: ColumnGroup[] = [
		{
			headerName: "Scheduled Tons",
			columns: [
				"ScheduledChargedTons",
				"ScheduledRolledTons",
				"ScheduledFinishedTons",
			],
		},
		{
			headerName: "Actual Tons",
			columns: ["ActualChargedTons", "ActualRolledTons", "ActualFinishedTons"],
		},
	];

	const columns: ColumnDef[] = [
		{
			field: "EventTypeCode",
			headerName: "Type",
			flex: 1,
			hide: true,
			render: (row) => {
				const typeCode = row.EventTypeCode;
				if (!typeCode) return null;
				const eventType = eventTypes.find((t) => t.code === typeCode);
				if (!eventType) return typeCode;
				return (
					<EventTypeTag
						event={{ Code: eventType.code, Name: eventType.name }}
						show="name"
					/>
				);
			},
		},
		{
			field: "Start",
			headerName: "Date",
			flex: 0.6,
			align: "center",
			spanRowsByDay: true,
			getValue: (row) => dxtToLocalServerTime(row.Start, "dd") || "",
			render: (row) => {
				if (row.IsMTDSummary || row.IsMonthSummary) return "";
				return dxtToLocalServerTime(row.Start, "dd") || "";
			},
			cellStyle: () => ({ verticalAlign: "middle" }),
		},
		{
			field: "SectionName",
			headerName: "Section",
			flex: 1,
			align: "center",
			spanRows: true,
			getValue: (row) => row.SectionName ?? row.EventTypeName,
			render: (row, _value) => {
				const displayValue = row.SectionName ?? row.EventTypeName;
				const canDrag =
					row.IsFirstRow && row.EventId > 0 && row.StatusCode === "P";
				return (
					<Box
						onContextMenu={(e) => {
							e.preventDefault();
							e.stopPropagation();
							if (row.SectionName && onSectionClick) {
								onSectionClick(row, { x: e.clientX, y: e.clientY });
							}
						}}
						sx={{
							cursor: row.SectionName ? "pointer" : "default",
							width: "100%",
							height: "100%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							position: "relative",
						}}
					>
						{canDrag && (
							<DragIndicator
								className="row-drag-handle"
								sx={{
									position: "absolute",
									left: 2,
									top: "50%",
									transform: "translateY(-50%)",
									fontSize: 16,
									color: "action.active",
									cursor: "grab",
									opacity: 0,
									transition: "opacity 0.15s ease",
									"&:active": { cursor: "grabbing" },
								}}
							/>
						)}
						{displayValue}
					</Box>
				);
			},
			cellStyle: (row) => {
				if (row.EventId < 0 || row.IsMTDSummary || row.IsMonthSummary)
					return {};

				const isProductionEvent = row.EventTypeCode === "P";
				const eventType = eventTypes.find((e) => e.code === row.EventTypeCode);
				const billetstatus = billetStatus.find(
					(s) => s.code === row.BilletsStatusCode,
				);

				const backgroundColor = isProductionEvent
					? (billetstatus?.backgroundColor ?? "transparent")
					: (eventType?.backgroundColor ?? "transparent");

				return {
					backgroundColor,
					fontWeight: row.EventTypeCode !== "P" ? "bold !important" : "normal",
				};
			},
		},
		{
			field: "BilletSize",
			headerName: "Sz",
			flex: 0.5,
			align: "center",
			spanRows: true,
		},
		{
			field: "InquiryStatusCode",
			headerName: "Sts",
			flex: 0.5,
			align: "center",
			spanRows: true,
		},
		{
			field: "Description",
			headerName: "Description",
			flex: 3,
			align: "left",
			spanRows: true,
		},
		// Scheduled Tons Group
		{
			field: "ScheduledChargedTons",
			headerName: "Charged",
			flex: 1,
			align: "center",
			group: "Scheduled Tons",
			spanRows: true,
			editable: (row) => row.EventTypeCode === "P" && row.StatusCode == "P",
			parseValue: (val) => (val === "" ? null : Number(val)),
			render: (row) => {
				const multi = isMultiMonthEvent(row);
				const total = formatTons(row.ScheduledChargedTons);
				if (!multi) return total || null;
				const ratio = getMonthRatio(row, calendarMonth);
				const monthVal = formatTons(
					Math.round((row.ScheduledChargedTons ?? 0) * ratio),
				);
				return renderMonthAndTotal(monthVal, total, true);
			},
		},
		{
			field: "ScheduledRolledTons",
			headerName: "Rolled",
			flex: 1,
			align: "center",
			group: "Scheduled Tons",
			spanRows: true,
			editable: (row) => row.EventTypeCode === "P" && row.StatusCode == "P",
			parseValue: (val) => (val === "" ? null : Number(val)),
			render: (row) => {
				const multi = isMultiMonthEvent(row);
				const total = formatTons(row.ScheduledRolledTons);
				if (!multi) return total || null;
				const ratio = getMonthRatio(row, calendarMonth);
				const monthVal = formatTons(
					Math.round((row.ScheduledRolledTons ?? 0) * ratio),
				);
				return renderMonthAndTotal(monthVal, total, true);
			},
		},
		{
			field: "ScheduledFinishedTons",
			headerName: "Finished",
			flex: 1,
			align: "center",
			group: "Scheduled Tons",
			spanRows: true,
			editable: (row) => row.EventTypeCode === "P" && row.StatusCode == "P",
			parseValue: (val) => (val === "" ? null : Number(val)),
			render: (row) => {
				const multi = isMultiMonthEvent(row);
				const total = formatTons(row.ScheduledFinishedTons);
				if (!multi) return total || null;
				const ratio = getMonthRatio(row, calendarMonth);
				const monthVal = formatTons(
					Math.round((row.ScheduledFinishedTons ?? 0) * ratio),
				);
				return renderMonthAndTotal(monthVal, total, true);
			},
		},
		// Actual Tons Group
		{
			field: "ActualChargedTons",
			headerName: "Charge",
			flex: 1,
			align: "center",
			group: "Actual Tons",
			spanRows: true,
			editable: (row) =>
				!isDraftMode && row.EventTypeCode === "P" && row.StatusCode !== "P",
			parseValue: (val) => (val === "" ? null : Number(val)),
			render: (row) => formatTons(row.ActualChargedTons) || null,
		},
		{
			field: "ActualRolledTons",
			headerName: "Rolled",
			flex: 1,
			align: "center",
			group: "Actual Tons",
			spanRows: true,
			editable: (row) =>
				!isDraftMode && row.EventTypeCode === "P" && row.StatusCode !== "P",
			parseValue: (val) => (val === "" ? null : Number(val)),
			render: (row) => formatTons(row.ActualRolledTons) || null,
		},
		{
			field: "ActualFinishedTons",
			headerName: "Finished",
			flex: 1,
			align: "center",
			group: "Actual Tons",
			spanRows: true,
			editable: (row) =>
				!isDraftMode && row.EventTypeCode === "P" && row.StatusCode !== "P",
			parseValue: (val) => (val === "" ? null : Number(val)),
			render: (row) => formatTons(row.ActualFinishedTons) || null,
		},
		{
			field: "ActualRollingHours",
			headerName: "Hour",
			flex: 1,
			align: "center",
			spanRows: true,
			hide: !canEditSchedule,
			editable: (row) =>
				canEditSchedule &&
				!isDraftMode &&
				row.EventTypeCode === "P" &&
				row.StatusCode !== "P",
			parseValue: (val) => (val === "" ? null : Number(val)),
			getValue: (row) =>
				row.StatusCode === "P"
					? Math.round(row.ScheduledHours ?? 0) || ""
					: Math.round(row.ActualHours ?? 0) || "",
		},
		{
			field: "BudgetTPH",
			headerName: "Budget TPH",
			flex: 1,
			align: "center",
			spanRows: true,
			hide: !canEditSchedule,
			editable: (row) =>
				canEditSchedule && row.EventTypeCode === "P" && row.StatusCode === "P",
			parseValue: (val) => (val === "" ? null : Number(val)),
		},
		{
			field: "TargetTPHPercentage",
			headerName: "Target TPH",
			flex: 1,
			align: "center",
			spanRows: true,
			getValue: getEffectiveTargetTPH,
		},
		{
			field: "ActualTPH",
			headerName: "Actual TPH",
			flex: 1,
			align: "center",
			spanRows: true,
			editable: (row) =>
				canEditSchedule &&
				!isDraftMode &&
				row.EventTypeCode === "P" &&
				row.StatusCode !== "P",
			parseValue: (val) => (val === "" ? null : Number(val)),
		},
		{
			field: "MillYield",
			headerName: "Mill Yield",
			flex: 1,
			align: "center",
			spanRows: true,
			getValue: (row) =>
				row.StatusCode === "P" ? row.ScheduledMillYield : row.ActualMillYield,
			formatValue: formatPercent,
		},
		{
			field: "FinishedYield",
			headerName: "Fin. Yield",
			flex: 1,
			align: "center",
			spanRows: true,
			getValue: (row) =>
				row.StatusCode === "P"
					? row.ScheduledFinishedYield
					: row.ActualFinishedYield,
			formatValue: formatPercent,
		},
		{
			field: "EventComments",
			headerName: "Comments",
			flex: 3,
			align: "left",
			spanRows: true,
			editable: true,
			render: (row) => {
				const value = row.EventComments ?? "";
				if (!value) return null;
				const isDown = value.toLowerCase().includes("down");
				return (
					<Tooltip title={value} arrow placement="top">
						<Box
							sx={{
								width: "100%",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								display: "flex",
								alignItems: "center",
								...(isDown && {
									backgroundColor: lighten(theme.palette.warning.main, 0.3),
								}),
							}}
						>
							{value}
						</Box>
					</Tooltip>
				);
			},
			cellStyle: (row) => {
				const eventComments = row.EventComments ?? "";
				if (eventComments.toLowerCase().includes("down")) {
					return {
						backgroundColor: lighten(theme.palette.warning.main, 0.3),
						fontWeight:
							row.EventTypeCode !== "P" ? "bold !important" : "normal",
					};
				}
				return {
					fontWeight: row.EventTypeCode !== "P" ? "bold !important" : "normal",
				};
			},
		},
		{
			field: "edit",
			headerName: "",
			width: 50,
			align: "center",
			spanRows: true,
			render: (row) => {
				if (row.EventId < 0) return null;
				return (
					<Tooltip title="Edit Event">
						<IconButton
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								const eventId = row.ParentEventId || row.EventId;
								if (eventId && eventId > 0) {
									handleEditClick?.(eventId);
								}
							}}
						>
							<EditIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				);
			},
		},
	];

	const visibleColumns = columns.filter((col) => !col.hide);

	return {
		columns,
		visibleColumns,
		columnGroups,
		getRowBackgroundColor,
	};
};
